import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ArrowRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => (n == null ? '-' : `$${Math.round(n).toLocaleString()}`);

const Stat = ({ label, value, tone = 'text-white' }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.14em] text-slate-300">{label}</p>
    <p className={`text-lg font-semibold mt-0.5 ${tone}`}>{value}</p>
  </div>
);

/**
 * What changing your major actually costs.
 *
 * This lived at the bottom of an institution's detail page, three clicks inside College
 * Intelligence, where the people making the decision never saw it. It belongs in front of anyone
 * whose transition *is* the major.
 */
export const MajorChangePanel = () => {
  const { getAuthHeader } = useAuth();
  const [school, setSchool] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [semesters, setSemesters] = useState(2);
  const [residency, setResidency] = useState('in_state');
  const [newMajor, setNewMajor] = useState('');
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = useCallback(async (term) => {
    setQuery(term);
    if (term.trim().length < 3) { setResults([]); return; }
    try {
      const r = await axios.get(`${API}/college-intel/search?q=${encodeURIComponent(term)}`, getAuthHeader());
      setResults((r.data?.schools || r.data?.results || []).slice(0, 6));
    } catch { setResults([]); }
  }, [getAuthHeader]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/transition/major-change`, {
        school_id: school?.id, extra_semesters: Number(semesters), residency,
        new_major: newMajor || undefined,
      }, getAuthHeader());
      setImpact(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not calculate that'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-amber-400/25 bg-slate-900/50 p-5 mb-6" data-testid="major-change-panel">
      <h3 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-300" />What switching majors would cost
      </h3>
      <p className="text-sm text-slate-300/80 mt-1 max-w-2xl">
        Changing direction is often the right call. It should be a decision made with the real
        number in front of you, added tuition, the earnings you'd have made in that time, and how
        far graduation moves.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-slate-300">Your institution</label>
          <div className="relative mt-1.5">
            <Search className="w-4 h-4 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={school ? school.name : query} onChange={e => { setSchool(null); search(e.target.value); }}
              className="pl-9" placeholder="Search your college" data-testid="major-change-school" />
          </div>
          {!school && results.length > 0 && (
            <div className="mt-1.5 rounded-lg border border-slate-700 divide-y divide-slate-800 overflow-hidden">
              {results.map(r => (
                <button key={r.id} onClick={() => { setSchool(r); setResults([]); }}
                  data-testid={`major-change-school-${r.id}`}
                  className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 transition-colors">
                  {r.name}<span className="text-slate-300 text-xs"> · {r.state || r.city}</span>
                </button>
              ))}
            </div>
          )}
          {!school && <p className="text-[11px] text-slate-300 mt-1.5">Leave blank to use the first college you've saved.</p>}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-slate-300">Major you'd switch to</label>
          <Input value={newMajor} onChange={e => setNewMajor(e.target.value)} className="mt-1.5"
            placeholder="e.g. Nursing" data-testid="major-change-new-major" />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-slate-300">Extra semesters it adds</label>
          <select value={semesters} onChange={e => setSemesters(e.target.value)}
            className="input-dark w-full mt-1.5" data-testid="major-change-semesters">
            {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} semester{n > 1 ? 's' : ''}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-slate-300">Residency</label>
          <select value={residency} onChange={e => setResidency(e.target.value)}
            className="input-dark w-full mt-1.5" data-testid="major-change-residency">
            <option value="in_state">In-state</option>
            <option value="out_of_state">Out-of-state</option>
          </select>
        </div>
      </div>

      <Button className="mt-4" onClick={run} disabled={busy} data-testid="major-change-run">
        {busy ? 'Calculating…' : <>What would this cost me <ArrowRight className="w-4 h-4 ml-1" /></>}
      </Button>

      {impact && (
        <div className="mt-5 rounded-xl border border-amber-400/30 bg-amber-400/[0.08] p-5" data-testid="major-change-result">
          <p className="text-base text-slate-100">{impact.explanation}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <Stat label="Added tuition" value={money(impact.added_tuition)} tone="text-amber-300" />
            <Stat label="Delayed earnings" value={money(impact.delayed_earnings)} tone="text-rose-300" />
            <Stat label="True cost of change" value={money(impact.total_cost_of_change)} />
            <Stat label="Graduation delay" value={`${impact.graduation_shift_months} months`} />
          </div>
          <p className="text-xs text-slate-300 mt-4">{impact.what_this_means}</p>
          {impact.attribution && (
            <p className="text-[11px] text-slate-300 mt-2">
              {typeof impact.attribution === 'string' ? impact.attribution
                : Object.values(impact.attribution).join(' · ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MajorChangePanel;
